import React, { useState } from 'react';
import { CType } from 'entities/card/model/types';
import { BlockReason } from 'features/card/block-card/model/types';
import { StopOutlined, EyeInvisibleOutlined, WarningOutlined, CreditCardOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Flex, List, Typography } from 'antd';
import { RoundContainer } from 'shared/ui';
import CardIcon from 'shared/ui/CardIcon/CardIcon';
import { useIntl } from 'react-intl';

type ReasonProps = {
    cardNumber: string;
    cardType: CType;
    onSubmit: (selectedReason: BlockReason) => unknown;
}

const Reason: React.FC<ReasonProps> = ({ cardNumber, cardType, onSubmit }) => {
    const [selectedReason, setSelectedReason] = useState<BlockReason>();
    const { formatMessage: t } = useIntl();

    const getReasonIcon = (reason: BlockReason) => {
        const isSelected = selectedReason === reason;
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: '2px solid #E5E6E9',
                    background: isSelected ? '#191919' : 'none',
                }}
            >
                {isSelected && (
                    <CheckOutlined style={{ fontSize: 16, color: '#fff' }} />
                )}
            </span>
        );
    };
    const reasons = [
        {
            text: t({id: 'reason.cardStolen'}),
            leftIcon: <RoundContainer size={40} color="#F2F3F5"><StopOutlined style={{ fontSize: 16, color: '#000' }} /></RoundContainer>,
            rightIcon: getReasonIcon(BlockReason.Stolen),
            onClick: () => setSelectedReason(BlockReason.Stolen)
        },
        {
            text: t({id: 'reason.cardLost'}),
            leftIcon: <RoundContainer size={40} color="#F2F3F5"><EyeInvisibleOutlined style={{ fontSize: 16, color: '#000' }} /></RoundContainer>,
            rightIcon: getReasonIcon(BlockReason.Lost),
            onClick: () => setSelectedReason(BlockReason.Lost)
        },
        {
            text: t({id: 'reason.fraudulent'}),
            leftIcon: <RoundContainer size={40} color="#F2F3F5"><WarningOutlined style={{ fontSize: 16, color: '#000' }} /></RoundContainer>,
            rightIcon: getReasonIcon(BlockReason.Fraudulent),
            onClick: () => setSelectedReason(BlockReason.Fraudulent)
        },
        {
            text: t({id: 'reason.cardCounterfeit'}),
            leftIcon: <RoundContainer size={40} color="#F2F3F5"><CreditCardOutlined style={{ fontSize: 16, color: '#000' }} /></RoundContainer>,
            rightIcon: getReasonIcon(BlockReason.Counterfeit),
            onClick: () => setSelectedReason(BlockReason.Counterfeit)
        },
    ];
    
    return (
        <Flex vertical gap={16} justify='space-between' align='stretch' style={{ flex: 1 }}>
            <div>
                <Flex vertical gap={8} style={{ marginBottom: 24 }}>
                    <Typography.Title level={3} >
                        { t({ id: 'reason.reason' }) }
                    </Typography.Title>
                    <Flex gap={12} align='center'>
                        <CardIcon cardNumber={cardNumber} />
                        <Typography.Text>{cardType === CType.Physical ? 'Plastic' : 'Virtual'} card *{cardNumber.slice(-4)}</Typography.Text>
                    </Flex>
                    <Typography.Text >
                        { t({ id: 'reason.newOne' }) }
                    </Typography.Text>
                </Flex>

                <List
                  dataSource={reasons}
                  renderItem={(item) => (
                    <List.Item onClick={item.onClick}>
                      <List.Item.Meta
                        avatar={item.leftIcon}
                        title={item.text}
                      />
                      {item.rightIcon}
                    </List.Item>
                  )}
                />
            </div>
            <Button disabled={!selectedReason} onClick={() => onSubmit(selectedReason as BlockReason)} style={{ backgroundColor: 'black', color: 'white' }}>
                { t({ id: 'reason.block' }) }
            </Button>
        </Flex>
    );
};

export default Reason;
